var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { useEffect } from "react";
import { usePrevious } from "@animech/ui";
import { ActionCreators as UndoActionCreators } from "redux-undo";
import { useAppDispatch, useAppSelector } from "../store";
import { actions } from "../actions";
import { selectors } from "../selectors";
import { tubeDiameterOptions, } from "../types";
import { UserSessionStorage } from "utility";
import { googleAnalyticsEvents, } from "utility/constants";
import { tubeConnectionModels } from "3d/constants/common";
export var Observers = function () {
    var dispatch = useAppDispatch();
    var activeSystem = useAppSelector(selectors.selectUserActiveSystem);
    var systemDataDefineParameters = useAppSelector(selectors.selectSystemDataDefineParameters);
    var systemDataSelectApplication = useAppSelector(selectors.selectSystemDataSelectApplication);
    var isCentralizedSystem = useAppSelector(selectors.selectIsCentralizedSystem);
    var objectShapes = useAppSelector(selectors.selectObjectShapes);
    var currentObjectShape = useAppSelector(selectors.selectObjectShape);
    useEffect(function () {
        if (systemDataDefineParameters)
            return;
        if (!systemDataSelectApplication)
            return;
        dispatch(actions.getSystemDataDefineParameters());
    }, [dispatch, systemDataSelectApplication, systemDataDefineParameters]);
    useEffect(function () {
        if (systemDataSelectApplication)
            return;
        dispatch(actions.getSystemDataSelectApplication());
    }, [dispatch, systemDataSelectApplication]);
    // objectShapes is no longer a static list with only metal objects. It will change depending on selected application.
    // therefore we need to reset current selected object to the first available object from the list whenever it updates.
    useEffect(function () {
        if (!objectShapes)
            return;
        var currentObjectExistsInObjectShapes = Object.keys(objectShapes).find(function (objectShape) { return objectShape === currentObjectShape; });
        // If the current object exists in the list (user probably just refreshed the page), then we don't need to reset the selection.
        if (currentObjectExistsInObjectShapes)
            return;
        dispatch(actions.setObjectShape(Object.keys(objectShapes)[0]));
    }, [currentObjectShape, dispatch, objectShapes]);
    var previousIsCentralizedSystem = usePrevious(isCentralizedSystem);
    useEffect(function () {
        if (previousIsCentralizedSystem === undefined ||
            previousIsCentralizedSystem === isCentralizedSystem)
            return;
        dispatch(actions.deleteAllEdges());
        dispatch(actions.clearPerformanceData());
        dispatch(actions.setOverlayData(undefined));
        /* We need to reset the undo-history everytime the user changes between centralized/decentralized.
         * To avoid being able to revert scene nodes that are incompatible with the tacton state. */
        dispatch(UndoActionCreators.clearHistory());
        /* Discard the matching delete snapshots so they can't be applied to a
         * now-incompatible history. */
        dispatch(actions.clearSnapshots());
    }, [isCentralizedSystem, previousIsCentralizedSystem, dispatch]);
    // We store the users activeSystem in session storage so that the information
    // persists through page reloads, but not when closing the window.
    useEffect(function () {
        if (activeSystem)
            return;
        var storedActiveSystem = UserSessionStorage.getActiveSystem();
        if (storedActiveSystem) {
            dispatch(actions.setUserActiveSystem(storedActiveSystem));
        }
    }, [activeSystem, dispatch]);
    var performanceData = useAppSelector(selectors.selectPerformanceData);
    var hasReachedPS = useAppSelector(selectors.selectHasReachedPartSecured);
    useEffect(function () {
        if (performanceData && hasReachedPS)
            logTaskPerformed(googleAnalyticsEvents.taskDone.calculation);
    }, [performanceData, hasReachedPS]);
    useUpdateDiameterOfTubeConnectionsObserver();
    useAddLatestSelectedCupObserver();
    return null;
};
/**
 * TODO: [PERFORMANCE] This observer is a source of slowdown when the user has a complex configuration.
 * A way to boost performance would be to not run this logic inside a useEffect, but instead run it
 * only at times when we know it is needed. That could be a performance boost at the cost of some
 * increased application complexity.
 */
function useUpdateDiameterOfTubeConnectionsObserver() {
    var dispatch = useAppDispatch();
    var tubeConnections = useAppSelector(selectors.selectTubeConnections);
    var attachments = useAppSelector(selectors.selectAttachments);
    var edges = useAppSelector(selectors.selectEdges);
    var unitSystem = useAppSelector(selectors.selectUnitSystem);
    useEffect(function () {
        var e_1, _a;
        var shouldNotUpdateAutomatically = function (tubeConnection) {
            return (tubeConnectionModels[tubeConnection.component.type]
                .type === "tool-changer");
        };
        var getConnectedEdges = function (tubeConnection) {
            return tubeConnection.attachmentIds
                .map(function (attachmentId) { var _a; return (_a = attachments[attachmentId]) === null || _a === void 0 ? void 0 : _a.edgeId; })
                .filter(function (edgeId) { return edgeId !== undefined; })
                .map(function (edgeId) { return edges[edgeId]; });
        };
        var hasNoConnectedEdges = function (edges) { return edges.length === 0; };
        var getEdgeDiameters = function (connectedEdges) {
            var fromEdgeToOuterDiameter = function (edge) {
                return edge.defaultDiameterIndex !== undefined
                    ? tubeDiameterOptions[unitSystem][edge.defaultDiameterIndex]
                        .outerValue
                    : edge.diameterDefaultTube;
            };
            return connectedEdges.map(fromEdgeToOuterDiameter);
        };
        var updateTubeConnectionDiameters = function (tubeConnection) {
            if (shouldNotUpdateAutomatically(tubeConnection))
                return;
            var connectedEdges = getConnectedEdges(tubeConnection);
            if (hasNoConnectedEdges(connectedEdges))
                return;
            var edgeDiameters = getEdgeDiameters(connectedEdges);
            var noDiameterHasChanged = !edgeDiameters.some(function (diameter, i) {
                return diameter !==
                    tubeConnection.component.diameters[i];
            });
            if (noDiameterHasChanged) {
                return;
            }
            dispatch(actions.setTubeConnectionDiameters({
                nodeIds: [tubeConnection.id],
                diameters: edgeDiameters,
            }));
        };
        try {
            for (var tubeConnections_1 = __values(tubeConnections), tubeConnections_1_1 = tubeConnections_1.next(); !tubeConnections_1_1.done; tubeConnections_1_1 = tubeConnections_1.next()) {
                var tubeConnection = tubeConnections_1_1.value;
                updateTubeConnectionDiameters(tubeConnection);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (tubeConnections_1_1 && !tubeConnections_1_1.done && (_a = tubeConnections_1.return)) _a.call(tubeConnections_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }, [edges, attachments, tubeConnections, dispatch, unitSystem]);
}
/**
 * Check whether there is a latest selected cup and latest added grip
 * position and add that cup to the latest added grip position.
 */
function useAddLatestSelectedCupObserver() {
    var dispatch = useAppDispatch();
    var systemDataDefineParameters = useAppSelector(selectors.selectSystemDataDefineParameters);
    var cupSelectionMap = useAppSelector(selectors.selectCupSelectionMap);
    var latestSelectedCup = useAppSelector(selectors.selectLatestSelectedCup);
    var latestAddedGripPosition = useAppSelector(selectors.selectLatestAddedGripPosition);
    var field = latestAddedGripPosition && cupSelectionMap[latestAddedGripPosition];
    var valueAlreadySet = field &&
        (systemDataDefineParameters === null || systemDataDefineParameters === void 0 ? void 0 : systemDataDefineParameters.suctionCupSelections[field]) ===
            latestSelectedCup;
    useEffect(function () {
        if (latestSelectedCup === undefined ||
            latestAddedGripPosition === undefined ||
            valueAlreadySet)
            return;
        dispatch(actions.selectCup({
            nodeIds: [latestAddedGripPosition],
            value: latestSelectedCup,
        }));
    }, [dispatch, latestAddedGripPosition, latestSelectedCup, valueAlreadySet]);
}
var logTaskPerformed = function (event) {
    if (!window.dataLayer) {
        return;
    }
    window.dataLayer.push({ event: event });
};
